import {DateTime} from 'luxon';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {
  build12Or24hFullOptionsForFormat,
  TimeFormatsEnum,
} from '../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {DateTimeFormatterBase} from '../../../../libs/shared/ui/src/components/datetime/dateTimeFormatterBase';
import {fontSizes} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../libs/shared/ui/src/styles/mixins';
import {ThemeContextType} from '../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Types.
 */

interface TimePickerItemProps {
  hour: DateTime;
  isSelectable?: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
  timeFormat: TimeFormatsEnum;
  timezone?: string;
}

/*
 * Style.
 */

interface ItemStyleProps {
  $isSelected?: boolean;
  $isSelectable?: boolean;
}
const StyledItemDiv = styled(ChromelessButton)<ItemStyleProps>`
  height: 30px;
  margin: 4px 0;

  border-radius: 5px;
  ${(p) => getBackgroundStyle(p)};
  color: ${(p) => getTextColor(p)};
  font-size: ${fontSizes.large};

  ${center()};
`;

function getTextColor(props: ItemStyleProps & ThemeContextType) {
  // Disabled state.
  if (!props.$isSelectable) {
    return props.theme.greys.shade50;
  }

  // Selected state.
  if (props.$isSelected) {
    return props.theme.greys.white;
  }

  // Default state.
  return props.theme.greys.shade80;
}

function getBackgroundStyle(props: ItemStyleProps) {
  // Disabled state.
  if (!props.$isSelectable) {
    return '';
  }

  // Selected state.
  if (props.$isSelected) {
    return css`
      background-color: ${(p) => p.theme.palette.blue.shade40};
    `;
  }

  // Default state with hover.
  return css`
    &:hover {
      background-color: ${(p) => p.theme.alphas.gray10};
    }
  `;
}

/*
 * Component.
 */

export const TimePickerItem: FC<TimePickerItemProps> = (props) => {
  const {hour, isSelected, isSelectable, onSelect, timeFormat, timezone} = props;
  const format = build12Or24hFullOptionsForFormat(timeFormat);

  return (
    <StyledItemDiv
      $isSelected={props.isSelected}
      $isSelectable={props.isSelectable}
      isActive={isSelected}
      isDisabled={!isSelectable}
      onClick={onSelect}
    >
      <DateTimeFormatterBase value={hour} format={format} timezone={timezone} />
    </StyledItemDiv>
  );
};
